import React, { useMemo, useEffect, useRef, useState } from "react";
import { brand } from "../../theme/theme";
import { useQueryClient } from "@tanstack/react-query";
import {
  Box,
  Stack,
  Typography,
  CircularProgress,
  Tooltip,
  IconButton,
  Divider,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  MessageSquare,
  Plus,
  Users,
  ChevronDown,
  Search,
  SquarePen,
  CheckCircle2,
  X,
  Archive,
  ArchiveRestore,
  Unlink,
} from "lucide-react";
import { Button } from "../../components/Button";
import { Avatar, AvatarFallback } from "../../components/Avatar";
import { CustomTextArea } from "../../components/CustomTextArea";
import { UnifiedModal } from "../../components/UnifiedModal";
import { ConfirmModal } from "../../components/ConfirmModal";
import { getData } from "../../components/hooks/getData";
import apiClient from "../../api-client";
import { UnreadThreadBadge } from "../../components/UnreadThreadBadge";
import { ReadOnlyBadge } from "../../components/ReadOnlyBadge";
import { isClientReadOnly } from "../../utils/clientAccess";

export function ClientAccordionItem({
  client,
  isExpanded,
  onToggle,
  selectedThreadId,
  selectedMessageThreadId = null,
  onThreadSelect,
  onCreateThread,
  onViewMembers,
  onUpdateThreadTitle,
  currentUser,
  unreadCount = 0,
  refreshKey,
  onSearchClient,
  onSearchThread,
  onShowNotification = null,
  onAutoSelectGeneralThread,
  shouldAutoSelectGeneral = false,
  onUnsyncMessageThread,
}) {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const shouldFetchThreads = Boolean(isExpanded);
  const threadsEndpoint = `communication/threads/?client=${client.id}`;
  const { data: fetchedThreads, isLoading } = getData(
    threadsEndpoint,
    shouldFetchThreads,
    [client.id, refreshKey]
  );

  const hasAutoSelectedRef = useRef(false);

  const threads = useMemo(() => {
    if (!fetchedThreads) return [];

    // Normaliza los datos recibidos en un array
    let rawThreads = [];
    if (Array.isArray(fetchedThreads)) {
      rawThreads = fetchedThreads;
    } else if (fetchedThreads?.results) {
      rawThreads = fetchedThreads.results;
    }

    // Asegura que el hilo de tipo "general" sea siempre el primero
    const sortedThreads = [...rawThreads].sort((a, b) => {
      if (a.type === "general") return -1;
      if (b.type === "general") return 1;
      return 0;
    });

    return sortedThreads;
  }, [fetchedThreads]);
  const [localThreads, setLocalThreads] = useState(threads);
  const [archiveThreadModalOpen, setArchiveThreadModalOpen] = useState(false);
  const [threadPendingArchive, setThreadPendingArchive] = useState(null);
  const [isDesyncAction, setIsDesyncAction] = useState(false);
  const [isArchivingThread, setIsArchivingThread] = useState(false);
  const [archiveThreadError, setArchiveThreadError] = useState("");
  const [archivedThreadsModalOpen, setArchivedThreadsModalOpen] = useState(false);
  const [archivedThreads, setArchivedThreads] = useState([]);
  const [isLoadingArchivedThreads, setIsLoadingArchivedThreads] = useState(false);
  const [isUnarchivingThread, setIsUnarchivingThread] = useState(false);

  useEffect(() => {
    setLocalThreads(threads);
  }, [threads]);

  const handleThreadTitleUpdate = async (
    threadId,
    newTitle,
    options = {}
  ) => {
    try {
      const isMessageThread = !!options?.isMessageThread;
      const updater = onUpdateThreadTitle
        ? onUpdateThreadTitle
        : async ({ threadId: id, title, isMessageThread: threadLike }) => {
            const endpoint = threadLike
              ? `communication/messages/${id}/`
              : `communication/threads/${id}/`;
            const payload = threadLike
              ? { is_thread_title: title }
              : { title };
            const response = await apiClient.patch(endpoint, payload);
            return response.data;
          };

      const result = await updater({
        threadId,
        clientId: client.id,
        title: newTitle,
        isMessageThread,
      });

      const resolvedTitle = result?.title ?? result?.is_thread_title ?? newTitle;
      if (resolvedTitle) {
        setLocalThreads((prevThreads) =>
          prevThreads.map((threadItem) =>
            String(threadItem.id) === String(threadId)
              ? { ...threadItem, title: resolvedTitle }
              : threadItem
          )
        );
      }

      return result;
    } catch (error) {
      console.error("Error actualizando título del hilo:", error);
      throw error;
    }
  };

  // Autoseleccionar thread general cuando se cargan los threads
  useEffect(() => {
    if (!shouldAutoSelectGeneral || !onAutoSelectGeneralThread) return;
    if (!isExpanded || threads.length === 0) return;
    if (hasAutoSelectedRef.current) return; // Ya se autoseleccionó

    // Buscar el thread de tipo "general"
    const generalThread = threads.find(thread => thread.type === "general");

    if (generalThread) {
      hasAutoSelectedRef.current = true;
      onAutoSelectGeneralThread(client.id, generalThread.id);
    }
  }, [threads, isExpanded, shouldAutoSelectGeneral, onAutoSelectGeneralThread, client.id]);

  // Reset del flag cuando se colapsa el acordeón
  useEffect(() => {
    if (!isExpanded) {
      hasAutoSelectedRef.current = false;
    }
  }, [isExpanded]);

  // Determinar si el usuario puede crear threads
  const isReadOnly =
    currentUser?.role !== "client" && isClientReadOnly(currentUser, client);
  const canCreateThreads = [
    "superadmin",
    "agency_admin",
    "consultant",
  ].includes(currentUser?.role);
  const canEditConversationTitle = canCreateThreads;
  const canManageArchivedThreads = ["superadmin", "agency_admin"].includes(
    currentUser?.role
  );

  const handleToggle = () => {
    onToggle();
  };

  const handleArchiveThread = (thread) => {
    if (!thread || !thread.id) return;
    const isMessageThread = !!thread?.is_message_thread;
    if (!canManageArchivedThreads) return;
    if (!isMessageThread && !["thematic", "project"].includes(thread.type)) return;

    setArchiveThreadError("");
    setIsDesyncAction(isMessageThread);
    setThreadPendingArchive(thread);
    setArchiveThreadModalOpen(true);
  };

  const handleConfirmArchiveThread = async () => {
    if (!threadPendingArchive?.id || isArchivingThread) return;
    setIsArchivingThread(true);
    setArchiveThreadError("");

    try {
      if (isDesyncAction) {
        if (onUnsyncMessageThread) {
          await onUnsyncMessageThread({
            messageId: threadPendingArchive.id,
            clientId: client.id,
          });
        } else {
          await apiClient.patch(`communication/messages/${threadPendingArchive.id}/`, {
            is_thread: false,
          });
        }
      } else {
        await apiClient.post(`communication/threads/${threadPendingArchive.id}/archive/`);
      }

      // Update local + cached threads only after backend success
      setLocalThreads((prevThreads) =>
        (prevThreads || []).filter(
          (item) => String(item.id) !== String(threadPendingArchive.id)
        )
      );

      queryClient.setQueriesData(
        { queryKey: ["getData", threadsEndpoint] },
        (oldData) => {
          if (!oldData) return oldData;

          const removeFromArray = (arr) =>
            (arr || []).filter(
              (item) => String(item?.id) !== String(threadPendingArchive.id)
            );

          if (Array.isArray(oldData)) {
            return removeFromArray(oldData);
          }

          if (oldData && typeof oldData === "object" && Array.isArray(oldData.results)) {
            return {
              ...oldData,
              results: removeFromArray(oldData.results),
            };
          }

          return oldData;
        }
      );

      setArchiveThreadModalOpen(false);
      setThreadPendingArchive(null);
      setIsDesyncAction(false);

      const removedMessageThreadWasSelected =
        isDesyncAction &&
        String(selectedMessageThreadId) === String(threadPendingArchive.id);
      const archivedWasSelected =
        !isDesyncAction &&
        String(selectedThreadId) === String(threadPendingArchive.id);
      if (removedMessageThreadWasSelected || archivedWasSelected) {
        const generalThread = (threads || []).find((t) => t.type === "general");
        const fallbackThreadId =
          threadPendingArchive?.parent_thread_id || generalThread?.id;
        if (fallbackThreadId) {
          onThreadSelect(client.id, fallbackThreadId, {
            toast: {
              message: isDesyncAction
                ? "Hilo desincronizado correctamente."
                : "Hilo archivado correctamente.",
              isError: false,
            },
          });
          return;
        }
      }

      if (onShowNotification) {
        onShowNotification({
          message: isDesyncAction
            ? "Hilo desincronizado correctamente."
            : "Hilo archivado correctamente.",
          isError: false,
        });
      }
    } catch (error) {
      console.error("Error archivando hilo:", error);
      const errorMessage =
        error?.response?.data?.detail ||
        (isDesyncAction
          ? "No se pudo desincronizar el hilo."
          : "No se pudo archivar el hilo.");
      setArchiveThreadError(errorMessage);
      if (onShowNotification) {
        onShowNotification({ message: errorMessage, isError: true });
      }
    } finally {
      setIsArchivingThread(false);
    }
  };

  const handleOpenArchivedThreads = async () => {
    setArchivedThreadsModalOpen(true);
    setIsLoadingArchivedThreads(true);
    try {
      const response = await apiClient.get(`communication/threads/archived/?client=${client.id}`);
      setArchivedThreads(response.data || []);
    } catch (error) {
      console.error("Error cargando hilos archivados:", error);
      if (onShowNotification) {
        onShowNotification({
          message: "No se pudieron cargar los hilos archivados.",
          isError: true,
        });
      }
    } finally {
      setIsLoadingArchivedThreads(false);
    }
  };

  const handleUnarchiveThread = async (threadId) => {
    if (isUnarchivingThread) return;
    setIsUnarchivingThread(true);

    try {
      await apiClient.post(`communication/threads/${threadId}/unarchive/`);

      // Remove from archived list
      setArchivedThreads((prev) =>
        prev.filter((t) => String(t.id) !== String(threadId))
      );

      // Invalidate the main threads query to refetch
      queryClient.invalidateQueries({ queryKey: ["getData", threadsEndpoint] });

      if (onShowNotification) {
        onShowNotification({
          message: "Hilo restaurado correctamente.",
          isError: false,
        });
      }
    } catch (error) {
      console.error("Error restaurando hilo:", error);
      const errorMessage =
        error?.response?.data?.detail || "No se pudo restaurar el hilo.";
      if (onShowNotification) {
        onShowNotification({ message: errorMessage, isError: true });
      }
    } finally {
      setIsUnarchivingThread(false);
    }
  };

  return (
    <Box data-client-id={client.id}>
      {/* Header del cliente */}
      <Box
        onClick={handleToggle}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "10px 16px",
          cursor: "pointer",
          borderTop: `1px solid ${theme.palette.divider}`,
          backgroundColor: isExpanded
            ? theme.palette.action.selected
            : "transparent",
          transition: "all 0.2s ease",
          "&:hover:not(:has(.client-action-button:hover))": {
            backgroundColor: theme.palette.action.hover,
          },
          position: "relative",
          borderRight: isExpanded
            ? `2px solid ${theme.palette.primary.main}`
            : "none",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flex: 1 }}>
          {/* Avatar más pequeño */}
          <Avatar
            sx={{ width: "32px", height: "32px", flexShrink: 0 }}
            src={client.logo}
          >
            <AvatarFallback
              sx={{
                backgroundColor: brand.avatar,
                color: "#fff",
                fontSize: "0.75rem",
                fontWeight: 600,
              }}
            >
              {client.name
                .split(" ")
                .map((n) => n[0])
                .join("")}
            </AvatarFallback>
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
              <Typography
                variant="body2"
                sx={{ fontWeight: 500, fontSize: "0.9rem" }}
              >
                {client.name}
              </Typography>
              {isReadOnly && <ReadOnlyBadge />}
            </Box>
            {client.agency && (
              <Typography
                variant="caption"
                sx={{
                  color: theme.palette.text.secondary,
                  fontSize: "0.75rem",
                }}
              >
                {client.agency.name}
              </Typography>
            )}
          </Box>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {/* Badge de mensajes no leídos - más discreto */}
          {unreadCount > 0 && (
            <Box onClick={(e) => e.stopPropagation()} sx={{ display: "inline-flex" }}>
              <UnreadThreadBadge count={unreadCount} />
            </Box>
          )}

          {onSearchClient && (
            <Tooltip title="Buscar en este cliente" arrow enterDelay={150}>
              <Button
                variant="ghost"
                size="sm"
                className="client-action-button"
                onClick={(e) => {
                  e.stopPropagation();
                  onSearchClient(client);
                }}
                sx={{
                  minWidth: "auto",
                  px: 1,
                  py: 0.5,
                  color: theme.palette.text.secondary,
                }}
              >
                <Search size={14} />
              </Button>
            </Tooltip>
          )}

          {/* Botón de ver participantes */}
          <Tooltip title="Ver participantes" arrow enterDelay={150}>
            <Button
              variant="ghost"
              size="sm"
              className="client-action-button"
              onClick={(e) => {
                e.stopPropagation();
                onViewMembers(client);
              }}
              sx={{
                minWidth: "auto",
                px: 1,
                py: 0.5,
                color: theme.palette.text.secondary,
              }}
            >
              <Users size={14} />
            </Button>
          </Tooltip>

          {/* Icono de chevron */}
          <ChevronDown
            size={16}
            style={{
              transition: "transform 0.2s ease",
              transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
              color: theme.palette.text.secondary,
            }}
          />
        </Box>
      </Box>

      {isExpanded && (
        <Box
          sx={{
            maxHeight: isExpanded ? 1200 : 0,
            opacity: isExpanded ? 1 : 0,
            overflow: "hidden",
            transition:
              "max-height 0.6s cubic-bezier(0.25, 0.1, 0.25, 1), opacity 0.45s ease-in-out",
            backgroundColor: theme.palette.background.default,
            position: "relative",
            pl: "16px",
          }}
        >
          {localThreads.length > 0 && (
            <Box
              sx={{
                position: "absolute",
                left: "31px",
                top: 10,
                bottom: canCreateThreads ? "52px" : "8px",
                width: "2px",
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.1)"
                    : "rgba(0, 0, 0, 0.08)",
                borderRadius: "1px",
              }}
            />
          )}

          {isLoading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                py: 3,
              }}
            >
              <CircularProgress size={24} />
            </Box>
          ) : (
            <>
              <Box sx={{ p: "8px 16px 8px 32px" }}>
                <Stack spacing={0.75}>
                  {localThreads.map((thread) => {
                    const isMessageThread = !!thread?.is_message_thread;
                    const isSelected = isMessageThread
                      ? String(selectedMessageThreadId) === String(thread.id)
                      : String(selectedThreadId) === String(thread.id) &&
                        !selectedMessageThreadId;

                    return (
                      <ThreadItem
                        key={
                          thread?.is_message_thread
                            ? `message-thread-${thread.id}`
                            : `thread-${thread.id}`
                        }
                        thread={thread}
                        isSelected={isSelected}
                        onClick={() => {
                          const targetThreadId = thread?.is_message_thread
                            ? thread?.parent_thread_id
                            : thread?.id;
                          onThreadSelect(client.id, targetThreadId, {
                            thread,
                            messageThreadId: thread?.is_message_thread
                              ? thread?.id
                              : null,
                            messageThreadTitle: thread?.is_message_thread
                              ? thread?.title || ""
                              : "",
                          });
                        }}
                        theme={theme}
                        onSearchThread={onSearchThread}
                        canEdit={canEditConversationTitle}
                        onEditThreadTitle={handleThreadTitleUpdate}
                        canArchive={canManageArchivedThreads}
                        onArchiveThread={handleArchiveThread}
                        unreadCount={thread.unopened_unread_notifications || 0}
                      />
                    );
                  })}

                  {localThreads.length === 0 && (
                    <Box
                      sx={{
                        padding: "24px 16px",
                        color: theme.palette.text.secondary,
                        fontSize: "0.85rem",
                      }}
                    >
                      No hay hilos temáticos.
                    </Box>
                  )}
                </Stack>
              </Box>

              {canCreateThreads && (
                <Box
                  sx={{
                    padding: "8px 16px 12px 0px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  {!isReadOnly && <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      onCreateThread(client);
                    }}
                    sx={{
                      flex: 1,
                      justifyContent: "flex-start",
                      color: theme.palette.text.secondary,
                      fontSize: "0.85rem",
                      py: 0.75,
                      "&:hover": {
                        color: theme.palette.primary.main,
                      },
                    }}
                  >
                    <Plus size={14} style={{ marginRight: 6 }} />
                    Nuevo hilo temático
                  </Button>}
                  {canManageArchivedThreads && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenArchivedThreads();
                      }}
                      sx={{
                        justifyContent: "flex-start",
                        color: theme.palette.text.secondary,
                        fontSize: "0.85rem",
                        py: 0.75,
                        px: 1.5,
                        "&:hover": {
                          color: theme.palette.primary.main,
                        },
                      }}
                    >
                      <Archive size={14} style={{ marginRight: 6 }} />
                      Ver archivados
                    </Button>
                  )}
                </Box>
              )}
            </>
          )}
        </Box>
      )}

      <ConfirmModal
        open={archiveThreadModalOpen}
        onClose={() => {
          setArchiveThreadModalOpen(false);
          setThreadPendingArchive(null);
          setIsDesyncAction(false);
          setArchiveThreadError("");
        }}
        onConfirm={handleConfirmArchiveThread}
        title={isDesyncAction ? "Desincronizar hilo" : "Archivar hilo"}
        question={
          isDesyncAction
            ? "¿Estás seguro de que quieres desincronizar este hilo?"
            : "¿Estás seguro de que quieres archivar este hilo?"
        }
        description={
          isDesyncAction
            ? "Se eliminará este hilo sincronizado de la lista y el mensaje se mantendrá en su hilo original."
            : "El hilo será archivado y ya no aparecerá en la lista de conversaciones."
        }
        tone="warning"
        confirmLabel={isDesyncAction ? "Desincronizar hilo" : "Archivar hilo"}
        loadingLabel={isDesyncAction ? "Desincronizando..." : "Archivando..."}
        loading={isArchivingThread}
      >
        {threadPendingArchive?.title && (
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
            {threadPendingArchive.title}
          </Typography>
        )}
        {archiveThreadError && (
          <Typography variant="body2" sx={{ color: theme.palette.error.main }}>
            {archiveThreadError}
          </Typography>
        )}
      </ConfirmModal>

      <UnifiedModal
        open={archivedThreadsModalOpen}
        onClose={() => {
          if (isUnarchivingThread) return;
          setArchivedThreadsModalOpen(false);
          setArchivedThreads([]);
        }}
        title="Hilos archivados"
        maxWidth="sm"
        disableExtremeZIndex
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {isLoadingArchivedThreads ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                py: 4,
              }}
            >
              <CircularProgress size={32} />
            </Box>
          ) : archivedThreads.length === 0 ? (
            <Box
              sx={{
                textAlign: "center",
                py: 4,
              }}
            >
              <Archive
                size={48}
                style={{
                  color: theme.palette.text.secondary,
                  marginBottom: 16,
                }}
              />
              <Typography
                variant="body1"
                sx={{ color: theme.palette.text.secondary }}
              >
                No hay hilos archivados
              </Typography>
            </Box>
          ) : (
            <Stack spacing={1}>
              {archivedThreads.map((thread) => (
                <Box
                  key={thread.id}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "12px 16px",
                    borderRadius: "8px",
                    backgroundColor: theme.palette.background.paper,
                    border: `1px solid ${theme.palette.divider}`,
                    transition: "all 0.2s ease",
                    "&:hover": {
                      backgroundColor: theme.palette.action.hover,
                    },
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {thread.title || "Sin título"}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: theme.palette.text.secondary }}
                    >
                      Archivado {new Date(thread.updated_at).toLocaleDateString()}
                    </Typography>
                  </Box>
                  <Tooltip title="Desarchivar" arrow enterDelay={150}>
                    <IconButton
                      size="small"
                      onClick={() => handleUnarchiveThread(thread.id)}
                      disabled={isUnarchivingThread}
                      sx={{
                        color: theme.palette.text.secondary,
                        "&:hover": {
                          backgroundColor: theme.palette.action.hover,
                          color: theme.palette.primary.main,
                        },
                      }}
                    >
                      <ArchiveRestore size={18} />
                    </IconButton>
                  </Tooltip>
                </Box>
              ))}
            </Stack>
          )}
        </Box>
      </UnifiedModal>
    </Box>
  );
}

// Componente para cada thread individual - rediseñado como card
function ThreadItem({
  thread,
  isSelected,
  onClick,
  theme,
  onSearchThread,
  canEdit,
  onEditThreadTitle,
  canArchive,
  onArchiveThread,
  unreadCount = 0,
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [titleValue, setTitleValue] = useState(
    thread.title ||
    (thread.type === "general" ? "Conversación principal" : "")
  );
  const [error, setError] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const isGeneralThread = thread.type === "general";
  const isMessageThread = !!thread?.is_message_thread;
  const canEditThread = canEdit && !isGeneralThread;
  const canDesyncThread =
    isMessageThread &&
    canArchive &&
    typeof onArchiveThread === "function";
  const canArchiveThread =
    !isMessageThread &&
    canArchive &&
    typeof onArchiveThread === "function" &&
    ["thematic", "project"].includes(thread.type);
  useEffect(() => {
    if (!isEditing) {
      setTitleValue(
        thread.title ||
        (thread.type === "general" ? "Conversación principal" : "")
      );
    }
  }, [thread.title, thread.type, isEditing]);

  const threadTitle =
    thread.type === "general"
      ? thread.title?.trim() || "Conversación principal"
      : thread.title?.trim() || "Sin título";

  const handleClick = (event) => {
    if (isEditing) {
      event.stopPropagation();
      return;
    }
    onClick();
  };

  const handleEditStart = (event) => {
    event.stopPropagation();
    if (!canEditThread) return;
    setError("");
    setIsEditing(true);
  };

  const handleCancel = (event) => {
    if (event) {
      event.stopPropagation();
    }
    setIsEditing(false);
    setError("");
    setTitleValue(
      thread.title ||
      (thread.type === "general" ? "Conversación principal" : "")
    );
  };

  const handleSave = async (event) => {
    if (event) {
      event.stopPropagation();
    }
    const trimmedTitle = titleValue.trim();

    if (!canEditThread) {
      return;
    }

    if (!trimmedTitle) {
      setError("El título no puede estar vacío.");
      return;
    }

    if (trimmedTitle.length < 3) {
      setError("El título debe tener al menos 3 caracteres.");
      return;
    }

    if (trimmedTitle === thread.title) {
      setIsEditing(false);
      return;
    }

    try {
      setIsSaving(true);
      await onEditThreadTitle?.(thread.id, trimmedTitle, { isMessageThread });
      setIsEditing(false);
      setError("");
    } catch (updateError) {
      const errorMessage =
        updateError?.message || "No se pudo actualizar el título.";
      setError(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Box
      onClick={handleClick}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.25,
        padding: "6px 8px",
        cursor: "pointer",
        borderRadius: "8px",
        backgroundColor: isSelected
          ? theme.palette.action.selected
          : "transparent",
        transition: "all 0.2s ease",
        "&:hover .client-thread-search-btn, &:hover .client-thread-edit-btn, &:hover .client-thread-archive-btn, &:hover .client-thread-desync-btn": {
          opacity: 1,
          transform: "translateX(0)",
        },
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {isEditing && canEditThread ? (
          <>
            <CustomTextArea
              value={titleValue}
              onChange={(event) => {
                setTitleValue(event.target.value);
                if (error) {
                  setError("");
                }
              }}
              rows={1}
              maxRows={3}
              autoExpand
              placeholder="Nuevo título de la conversación"
              autoFocus
              onClick={(event) => event.stopPropagation()}
              onKeyDown={(event) => {
                if (event.key === "Enter" && !event.shiftKey) {
                  event.preventDefault();
                  handleSave(event);
                }
                if (event.key === "Escape") {
                  event.preventDefault();
                  handleCancel(event);
                }
              }}
              sx={{
                fontSize: "0.85rem",
                minHeight: "34px",
              }}
            />
            {error && (
              <Typography
                variant="caption"
                sx={{
                  color: theme.palette.error.main,
                  display: "block",
                  marginTop: "4px",
                }}
                onClick={(event) => event.stopPropagation()}
              >
                {error}
              </Typography>
            )}
          </>
        ) : (
          <Tooltip title={threadTitle} arrow enterDelay={250}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: isSelected ? 600 : 500,
                fontSize: "0.85rem",
                color: theme.palette.text.primary,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {threadTitle}
            </Typography>
          </Tooltip>
        )}
      </Box>

      {isEditing && canEditThread ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0,
            flexShrink: 0,
          }}
        >
          <Tooltip title="Guardar título" arrow enterDelay={150}>
            <IconButton
              size="small"
              onClick={handleSave}
              disabled={isSaving}
              sx={{
                padding: "4px",
                color: theme.palette.text.secondary,
                "&:hover": {
                  backgroundColor: theme.palette.action.hover,
                },
              }}
            >
              {isSaving ? (
                <CircularProgress size={14} />
              ) : (
                <CheckCircle2 size={16} />
              )}
            </IconButton>
          </Tooltip>
          <Tooltip title="Cancelar edición" arrow enterDelay={150}>
            <IconButton
              size="small"
              onClick={handleCancel}
              sx={{
                padding: "4px",
                color: theme.palette.text.secondary,
                "&:hover": {
                  backgroundColor: theme.palette.action.hover,
                },
              }}
            >
              <X size={16} />
            </IconButton>
          </Tooltip>
        </Box>
      ) : (
        <Box sx={{ display: "flex", alignItems: "center", gap: "5px", flexShrink: 0 }}>
          {unreadCount > 0 && !isSelected && (
            <UnreadThreadBadge count={unreadCount} />
          )}
          {onSearchThread && (
            <Tooltip title="Buscar mensajes en este hilo" arrow enterDelay={150}>
              <IconButton
                size="small"
                className="client-thread-search-btn"
                onClick={(event) => {
                  event.stopPropagation();
                  onSearchThread(thread);
                }}
                sx={{
                  opacity: 0,
                  transform: "translateX(8px)",
                  transition: "all 0.2s ease",
                  color: theme.palette.text.secondary,
                  "&:hover": {
                    backgroundColor: theme.palette.action.hover,
                  },
                  width: "22px",
                  height: "22px",
                  padding: "2px",
                  borderRadius: "6px",
                }}
              >
                <Search size={14} />
              </IconButton>
            </Tooltip>
          )}
          {canEditThread && (
            <Tooltip title="Editar título" arrow enterDelay={150}>
              <IconButton
                size="small"
                className="client-thread-edit-btn"
                onClick={(event) => {
                  event.stopPropagation();
                  handleEditStart(event);
                }}
                sx={{
                  opacity: 0,
                  transform: "translateX(8px)",
                  transition: "all 0.2s ease",
                  color: theme.palette.text.secondary,
                  "&:hover": {
                    backgroundColor: theme.palette.action.hover,
                  },
                  width: "22px",
                  height: "22px",
                  padding: "2px",
                  borderRadius: "6px",
                }}
              >
                <SquarePen size={14} />
              </IconButton>
            </Tooltip>
          )}
          {canArchiveThread && (
            <Tooltip title="Archivar hilo" arrow enterDelay={150}>
              <IconButton
                size="small"
                className="client-thread-archive-btn"
                onClick={(event) => {
                  event.stopPropagation();
                  onArchiveThread(thread);
                }}
                sx={{
                  opacity: 0,
                  transform: "translateX(8px)",
                  transition: "all 0.2s ease",
                  color: theme.palette.text.secondary,
                  "&:hover": {
                    backgroundColor: theme.palette.action.hover,
                  },
                  width: "22px",
                  height: "22px",
                  padding: "2px",
                  borderRadius: "6px",
                }}
              >
                <Archive size={14} />
              </IconButton>
            </Tooltip>
          )}
          {canDesyncThread && (
            <Tooltip title="Desincronizar hilo" arrow enterDelay={150}>
              <IconButton
                size="small"
                className="client-thread-desync-btn"
                onClick={(event) => {
                  event.stopPropagation();
                  onArchiveThread(thread);
                }}
                sx={{
                  opacity: 0,
                  transform: "translateX(8px)",
                  transition: "all 0.2s ease",
                  color: theme.palette.error.main,
                  "&:hover": {
                    backgroundColor: `${theme.palette.error.main}1A`,
                    color: theme.palette.error.dark,
                  },
                  width: "22px",
                  height: "22px",
                  padding: "2px",
                  borderRadius: "6px",
                }}
              >
                <Unlink size={14} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      )}
    </Box>
  );
}
